import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, PenTool, Newspaper, Brain, Network, Images } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { useExam } from '@/context/ExamContext';

const MobileBottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { isSI } = useExam();

  // ── Bug fix: anonymous (non-logged-in) visitors used to see this
  // in-app navigation on every public marketing route (/upsc, /about,
  // /pricing, /auth, /upgrade, etc.), which then bounced them to the
  // login screen when tapped. The in-app bottom nav is for
  // authenticated users ONLY. Anonymous users see the public footer.
  if (!user) return null;

  // SI is an objective/OMR exam — no descriptive Mains or UPSC Optionals, and
  // the "Prelims" section is labelled "GS" for SI aspirants.
  const navItems = isSI
    ? [
        { path: '/dashboard', icon: Home, label: 'Home' },
        { path: '/mindmap', icon: Network, label: 'Mindmaps' },
        { path: '/visual-resource', icon: Images, label: 'Visual' },
        { path: '/prelims', icon: FileText, label: 'GS' },
        { path: '/csat-suite', icon: Brain, label: 'Aptitude' },
        { path: '/daily-intelligence', icon: Newspaper, label: 'News' },
      ]
    : [
        { path: '/dashboard', icon: Home, label: 'Home' },
        { path: '/mindmap', icon: Network, label: 'Mindmaps' },
        { path: '/visual-resource', icon: Images, label: 'Visual' },
        { path: '/prelims', icon: FileText, label: 'Prelims' },
        { path: '/mains', icon: PenTool, label: 'Mains' },
        { path: '/daily-intelligence', icon: Newspaper, label: 'News' },
      ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Also hide on public marketing routes (just in case a logged-in user
  // visits them) + auth flows + state-PSC routes that have their own nav.
  const hiddenExact = ['/', '/upsc', '/about', '/pricing', '/upgrade'];
  const hiddenPrefix = [
    '/auth', '/login', '/register', '/forgot-password', '/reset-password',
    '/tgpsc', '/appsc',
    '/support', '/help', '/terms-of-service', '/privacy-policy',
    '/refund-policy', '/launch-compliance',
    '/partnership-pricing', '/institute-pricing',
  ];
  const shouldHide =
    hiddenExact.includes(location.pathname) ||
    hiddenPrefix.some(p => location.pathname.startsWith(p));
  if (shouldHide) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200/50 z-50 sm:hidden safe-area-bottom shadow-lg">
      <div className="flex items-center justify-around py-1.5 px-1">
        {navItems.map(({ path, icon: Icon, label }) => {
          const active = isActive(path);
          return (
            <Link
              key={path}
              to={path}
              className={`flex flex-col items-center justify-center py-1 px-2 rounded-lg transition-all duration-200 min-w-[50px] ${
                active 
                  ? 'scale-105' 
                  : 'text-slate-500 active:scale-95'
              }`}
            >
              <div className={`p-1.5 rounded-lg transition-all duration-200 ${
                active 
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-500 shadow-md' 
                  : ''
              }`}>
                <Icon 
                  size={20} 
                  strokeWidth={active ? 2.5 : 2}
                  className={active ? 'text-white' : 'text-slate-500'}
                />
              </div>
              <span className={`text-[9px] font-medium mt-0.5 ${
                active ? 'text-indigo-600' : 'text-slate-500'
              }`}>
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
